import classNames from "classnames";
import { useState } from "react";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { CaretDown } from "components/icons/CaretDown";
import { CLICKED_ENTITY_SWITCHER_ON_FILINGS_TIMELINE } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { entitiesSortedByCountry } from "utils/entitiesSortedByCountry";

type EntityScopeSelectorProps = {
  selectedEntityIds: string[];
  onChange: (entityIds: string[]) => void;
};

const ALL_ENTITIES_LABEL = "All entities";
const ALL_ENTITIES_VALUE = "all";
const LAST_ENTITY_MESSAGE = "At least one entity is required.";

const TRIGGER_CLASS =
  "!t-justify-between !t-gap-2 !t-rounded-lg !t-border !t-border-solid !t-border-neutral-10 !t-bg-neutral-66 !t-px-3 !t-font-normal !t-shadow-none";

export const EntityScopeSelector = ({
  selectedEntityIds,
  onChange,
}: EntityScopeSelectorProps) => {
  const { entities: allEntities } = useCurrentGroupContext();
  const { trackEvent } = useAnalytics();

  const { usEntities: entities } = entitiesSortedByCountry(allEntities);

  const [isOpen, setIsOpen] = useState(false);

  const handleTriggerClick = () =>
    trackEvent(CLICKED_ENTITY_SWITCHER_ON_FILINGS_TIMELINE);

  const isAllSelected =
    selectedEntityIds.length === 0 ||
    selectedEntityIds.length === entities.length;

  const selectedNames = entities
    .filter((entity) => selectedEntityIds.includes(entity.uuid))
    .map((entity) => entity.name);

  const triggerLabel = isAllSelected
    ? ALL_ENTITIES_LABEL
    : selectedNames.join(", ");

  const isOnlySelected = !isAllSelected && selectedEntityIds.length === 1;

  const selectAllEntities = () => onChange([]);

  const handleToggleEntity = (entityId: string) => () => {
    const currentIds = isAllSelected
      ? entities.map((entity) => entity.uuid)
      : selectedEntityIds;

    const nextIds = currentIds.includes(entityId)
      ? currentIds.filter((id) => id !== entityId)
      : [...currentIds, entityId];

    onChange(nextIds.length === entities.length ? [] : nextIds);
  };

  const rows = [
    {
      value: ALL_ENTITIES_VALUE,
      label: ALL_ENTITIES_LABEL,
      checked: isAllSelected,

      isLocked: isAllSelected,
      onToggle: selectAllEntities,
    },
    ...entities.map((entity) => {
      const isChecked =
        isAllSelected || selectedEntityIds.includes(entity.uuid);

      return {
        value: entity.uuid,
        label: entity.name,
        checked: isChecked,
        isLocked: isChecked && isOnlySelected,
        onToggle: handleToggleEntity(entity.uuid),
      };
    }),
  ];

  return (
    <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dropdown.Trigger asChild className="t-group">
        <div>
          <Button
            customType="secondary"
            size="regular"
            className={TRIGGER_CLASS}
            onClick={handleTriggerClick}
          >
            <ToolTip text={triggerLabel}>
              <span className="t-max-w-[124px] t-truncate t-text-left t-text-body t-text-text-60">
                {triggerLabel}
              </span>
            </ToolTip>
            <span className="t-flex t-text-text-30 group-data-state-open:t-rotate-180">
              <CaretDown width="12" height="12" />
            </span>
          </Button>
        </div>
      </Dropdown.Trigger>

      <Dropdown.Portal>
        <Dropdown.Content
          align="end"
          sideOffset={8}
          className="!t-min-w-56 t-max-h-96"
        >
          {rows.map(({ value, label, checked, isLocked, onToggle }) => (
            <ConditionalToolTip
              key={value}
              condition={isLocked && LAST_ENTITY_MESSAGE}
            >
              <div
                className={classNames("t-px-3 t-py-2", {
                  "t-ml-4": value !== ALL_ENTITIES_VALUE,
                })}
              >
                <Checkbox
                  label={label}
                  name={value}
                  checked={checked}
                  disabled={isLocked}
                  onChange={onToggle}
                />
              </div>
            </ConditionalToolTip>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};
